import type { ReactNode } from 'react';
import type { To } from 'react-router';

import { selectors } from '../../common/selectors';
import { transitions } from '../../common/transitions';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';

const OverflowContainer = styled(Small, {
  display: 'flex',
  alignSelf: 'center',
  padding: '$4 $8',
  borderRadius: '$8',
  cursor: 'pointer',
  color: colors.linkAlternativeInitialLight,
  textAlign: 'center',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.linkAlternativeInitialDark,
  },

  [selectors.hover]: {
    background: colors.bgAlternativeLight,
    color: colors.linkAlternativeInteractiveLight,

    [darkThemeSelector]: {
      background: colors.bgAlternativeDark,
      color: colors.linkAlternativeInteractiveDark,
    },
  },

  [selectors.focus]: {
    boxShadow: shadows.focusRingLight,
    outline: 'none',
    color: colors.linkAlternativeInteractiveLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
      color: colors.linkAlternativeInteractiveDark,
    },
  },
});
OverflowContainer.displayName = 'OverflowContainer';

export type OverflowProps = {
  /**
   * The label of the overflow.
   */
  label?: ReactNode;
  /**
   * Show a custom label instead of the default "View all {total} {label}"
   */
  labelOverride?: ReactNode;
  /**
   * The total number of items.
   */
  total?: number;
  // Routing
  as?: React.ComponentType<any>;
  to?: To;
  onClick?: () => void;
};

export function Overflow({ label, labelOverride, total, as, to, onClick }: OverflowProps) {
  const isPressable = Boolean((as && to) || onClick);

  return (
    <OverflowContainer as={as} to={to} onClick={onClick} tabIndex={isPressable ? 0 : undefined}>
      {labelOverride ?? `View all ${total} ${label ?? 'items'}`}
    </OverflowContainer>
  );
}
